import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "./hooks/useTheme";
import { BusinessConfigContext } from "./context/BusinessConfigContext";
import businessRegistry from "./config/businessRegistry";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import FloatingButtons from "./components/FloatingButtons";
import "bootstrap/dist/css/bootstrap.min.css";
import "./styles/global.css";

// Lazy-loaded pages
const Landing      = lazy(() => import("./pages/Landing"));
const Home         = lazy(() => import("./pages/Home"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const GalleryPage  = lazy(() => import("./pages/GalleryPage"));
const ContactPage  = lazy(() => import("./pages/ContactPage"));
const ProductsPage = lazy(() => import("./pages/ProductsPage"));
const NotFound     = lazy(() => import("./pages/NotFound"));

const PageLoader = () => (
  <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
    <div style={{ textAlign: "center" }}>
      <div className="skeleton" style={{ width: 200, height: 20, marginBottom: 12 }} />
      <div className="skeleton" style={{ width: 140, height: 16 }} />
    </div>
  </div>
);

/**
 * BusinessLayout — resolves the slug, injects config via context,
 * applies the theme, and renders Navbar + page routes + Footer.
 */
const BusinessLayout = () => {
  const { slug } = useParams();
  const config = businessRegistry[slug];

  // Unknown slug → 404
  if (!config) {
    return (
      <Suspense fallback={<PageLoader />}>
        <NotFound />
      </Suspense>
    );
  }

  return (
    <BusinessConfigContext.Provider value={config}>
      <ThemeProvider config={config}>
        <Navbar />
        <main>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route index          element={<Home />} />
              <Route path="services"  element={<ServicesPage />} />
              <Route path="gallery"   element={<GalleryPage />} />
              <Route path="contact"   element={<ContactPage />} />
              <Route path="products"  element={<ProductsPage />} />
              <Route path="*"         element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
        <FloatingButtons />
      </ThemeProvider>
    </BusinessConfigContext.Provider>
  );
};

const App = () => (
  <HelmetProvider>
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Landing page — lists all available business demos */}
          <Route path="/" element={<Landing />} />

          {/* Each business gets its own nested route tree */}
          <Route path="/:slug/*" element={<BusinessLayout />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  </HelmetProvider>
);

export default App;
